// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
// RxJS
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Auth
import { AuthNoticeService, AuthService } from '../../../../core/auth';
import { getEmailErrorMessage } from '../../../../core/auth/_utils/auth-validation.util';
import { getBrandingLogoUrl } from '../../../../utils/general-utils';
import { environment } from '../../../../../environments/environment';

@Component({
	selector: 'kt-forgot-password',
	templateUrl: './forgot-password.component.html',
	encapsulation: ViewEncapsulation.None
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
	// Public params
	forgotPasswordForm: UntypedFormGroup;
	loading = false;
	errors: any = [];
	gCaptchaResponse: any;
	isCaptchaSelected: boolean = true
	siteKey = environment.runtime.siteKey
	recaptcha: boolean;
	logoUrl: string = '';
	emailErrorMessage: string = '';

	imagePaths = {
		uptimeLogoOriginal: "assets/media/logos/logo-loader-colour.svg"
	}

	private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param authService
	 * @param authNoticeService
	 * @param translate
	 * @param router
	 * @param fb
	 * @param cdr
	 */
	constructor(
		private readonly authService: AuthService,
		public authNoticeService: AuthNoticeService,
		private readonly translate: TranslateService,
		private readonly router: Router,
		private readonly fb: UntypedFormBuilder,
		private readonly cdr: ChangeDetectorRef
	) {
		this.unsubscribe = new Subject();
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit() {
		this.authNoticeService.setNotice(null);
		this.recaptcha = environment.runtime.recaptcha;
		this.initRegistrationForm();
		let rawLogo: string | undefined;
		try {
			rawLogo = JSON.parse(localStorage.getItem('layoutConfig') || '{}')?.['logo'];
		} catch (e) {
			rawLogo = undefined;
		}
		this.logoUrl = getBrandingLogoUrl(rawLogo);

		this.forgotPasswordForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.updateErrors();
		});
		this.updateErrors();
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.unsubscribe.next(undefined);
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initRegistrationForm() {
		const storedEmail = sessionStorage.getItem('femail') || '';
		this.forgotPasswordForm = this.fb.group({
			email: [storedEmail, Validators.compose([
				Validators.required,
				Validators.email,
				Validators.minLength(3),
				Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
			])
			]
		});
	}

	resolved(e) {
		this.gCaptchaResponse = e
		this.isCaptchaSelected = e ? false : true;
	}

	/**
	 * Form Submit
	 */
	submit() {
		const controls = this.forgotPasswordForm.controls;
		/** check form */
		if (this.forgotPasswordForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			this.updateErrors();
			return;
		}

		this.loading = true;

		const email = controls['email'].value;
		this.authService.requestPassword(email.toLowerCase(), this.gCaptchaResponse).pipe(
			tap(response => {
				if (response.status == 200) {
					sessionStorage.setItem('femail', email)
					this.router.navigateByUrl('/web/reset-password');
				} else {
					if (environment.runtime.recaptcha)
						grecaptcha.reset()
					this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.NOT_FOUND', { name: this.translate.instant('AUTH.INPUT.EMAIL') }), 'danger');
				}
			}),
			takeUntil(this.unsubscribe),
			finalize(() => {
				this.loading = false;
				this.cdr.detectChanges();
			})
		).subscribe();
	}

	updateErrors(): void {
		this.emailErrorMessage = getEmailErrorMessage(this.forgotPasswordForm.get('email'), this.translate);
	}
}
